<template>
    <div class="relative flex items-center justify-center overflow-hidden"
        :class="[sizeClasses, { 'cursor-pointer': clickable, ' border border-white': !hasAnimalAvatar && !hideBackground, 'rounded-full': rounded }, className]"
        :style="hasAnimalAvatar ? {} : avatarStyle" @click="$emit('click', $event)">
        <!-- Animal Avatar Display (if configured) -->
        <AvatarDisplay v-if="hasAnimalAvatar" :animal="currentAnimal" :pose="currentPose" :accessories="currentAccessories"
            :background="currentBackground" :size="avatarDisplaySize" class="w-full h-full" :shape="getShape" />

        <!-- Legacy photo avatar (if available and no animal avatar) -->
        <img v-else-if="legacyPhotoUrl" :src="legacyPhotoUrl" :alt="child?.name" loading="lazy" decoding="async"
            class="w-full h-full object-cover">

        <!-- Fallback with initial and color -->
        <div v-else class="w-full h-full flex items-center justify-center font-bold  overflow-hidden"
            :class="[textColorClass]" :style="fallbackStyle">
            {{ childInitial }}
        </div>


        <div class="absolute -bottom-1 -right-1" v-if="showPoints && (child?.points || child?.points === 0)">
            <Badge variant="default" class="text-xs px-1.5 py-0.5">{{ child.points || 0 }}</Badge>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { getChildColorInfo } from '@/lib/colors';
import type { Child } from '@/models/Child';
import AvatarDisplay from '@/components/avatar/AvatarDisplay.vue';
import { useAvatarsStore } from '@/stores/avatars';

const props = withDefaults(defineProps<{
    child: Child | null;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    showPoints?: boolean;
    clickable?: boolean;
    displayName?: boolean;
    className?: string;
    hideBackground?: boolean; // Hide avatar background image
    rounded?: boolean;
}>(), {
    size: 'md',
    showPoints: true,
    clickable: false,
    hideBackground: false,
    rounded: false
});

defineEmits(['click']);

const avatarStore = useAvatarsStore();

// Load avatar when child changes
watch(
    () => props.child?.id,
    async (childId) => {
        if (childId) {
            await avatarStore.loadChildAvatar(childId);
        }
    },
    { immediate: true }
);

// Get size classes based on size prop
const sizeClasses = computed(() => {
    const sizes = {
        xs: 'w-6 h-6 text-xs',
        sm: 'w-8 h-8 text-sm',
        md: 'w-12 h-12 text-lg',
        lg: 'w-16 h-16 text-2xl',
        xl: 'w-24 h-24 text-4xl',
        '2xl': 'w-32 h-32 text-5xl',
        '3xl': 'w-40 h-40 text-6xl',
        '4xl': 'w-48 h-48 text-7xl',
        '5xl': 'w-56 h-56 text-8xl'
    };
    return sizes[props.size];
});

// Map size prop to AvatarDisplay size
const avatarDisplaySize = computed(() => {
    const mapping = {
        xs: 'sm',
        sm: 'sm',
        md: 'md',
        lg: 'md',
        xl: 'lg',
        '2xl': 'xl',
        '3xl': 'xl',
        // Au-delà, l'image REMPLIT le cadre. Sans entrée ici, elle retombait
        // sur le plafond de 150px du gabarit par défaut, quelle que soit la
        // taille demandée (la fête de la journée bouclée le montrait).
        '4xl': 'full',
        '5xl': 'full'
    };
    return mapping[props.size] as 'sm' | 'md' | 'lg' | 'xl' | 'full';
});

const getShape = computed(() => {
    if (props.rounded) return 'rounded';
    return 'square';
});

// Check if child has animal avatar configured
const hasAnimalAvatar = computed(() => {
    return !!avatarStore.getAnimalForChild(props.child?.id);
});

const currentAnimal = computed(() => avatarStore.getAnimalForChild(props.child?.id));
const currentPose = computed(() => avatarStore.getPoseForChild(props.child?.id));
const currentAccessories = computed(() => avatarStore.getAccessoriesForChild(props.child?.id));
const currentBackground = computed(() => {
    // If hideBackground prop is true, don't show background
    if (props.hideBackground) {
        return null;
    }
    return avatarStore.getBackgroundForChild(props.child?.id);
});

// `children.avatar` ne contient PAS une photo mais l'identifiant de l'animal
// choisi à la création ("dog", "fox"…) : sur 459 enfants en production, aucun ne
// porte d'URL. Passé tel quel en `src`, il donnait une image cassée partout où
// la config d'avatar n'était pas encore chargée. On ne l'affiche donc que quand
// c'en est vraiment une, et on retombe sinon sur l'initiale.
const legacyPhotoUrl = computed(() => {
    const value = props.child?.avatar;
    if (!value) return null;
    return /^(https?:|data:|blob:|\/)/i.test(value) ? value : null;
});

// Get child's first initial
const childInitial = computed(() => {
    return props.child?.name?.charAt(0)?.toUpperCase() || '?';
});

// Get color info for the child's color or default (always returns a valid object)
const colorInfo = computed(() => {
    const colorValue = props.child?.color || 'blue';
    return getChildColorInfo(colorValue);
});

// Style for the avatar background (guard against missing colorInfo)
const fallbackStyle = computed(() => {
    const info = colorInfo.value;
    if (!info) return { backgroundColor: '#e5e7eb', color: '#1f2937' };
    return {
        backgroundColor: info.hex300,
        color: '#1f2937',
    };
});

// Text color class — child palette's readable text shade
const textColorClass = computed(() => 'text-[var(--child-900)]');

// Avatar style (can be overridden with custom styles)
const avatarStyle = computed(() => {
    const info = colorInfo.value;
    if (!info) return { backgroundColor: '#e5e7eb' };
    return {
        backgroundColor: props.child?.color ? info.hex300 : '#e5e7eb',
    };
});
</script>
